import React, { useCallback, useEffect, useRef } from 'react';

import { ButtonWithSpinner } from '@eika/button-with-spinner';

import type { Account, AccountId } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAccountTransferFrom } from '../../../services/accountFilters';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import { hasSigningError } from '../Payment/PaymentForm';
import { PaymentRetryContext } from '../PaymentRetry';
import TransactionResultInfo from '../TransactionResultInfo';
import InvalidFromAccountInfo, { TransactionType } from '../Transfer/InvalidFromAccountInfo';
import MissingToAccountInfo from '../Transfer/MissingToAccountInfo';
import useCanTransferMoney, { CanTransferMoney } from '../Transfer/useCanTransferMoney';
import RecurringTransferFields from './RecurringTransferFields';
import createRecurringTransferRequest from './createRecurringTransferRequest';
import { RecurringTransferActionType, SetAccountType } from './form/actions';
import useFormContext from './form/useFormContext';

import './CreateRecurringTransferForm.scss';

export const ANALYTICS_CATEGORY = 'recurring-transfer';

interface Props {
    onClose: (openPayment?: boolean) => void;
    account?: Account;
}

const CreateRecurringTransferForm: React.FC<Props> = ({ onClose, account }) => {
    const translated = useTranslation();
    const fromAccountId = useRef<AccountId>();

    const canTransferMoney = useCanTransferMoney();

    const { state, handleChange, checkFormValidity } = useFormContext();
    const {
        handleCancelSigningFlow,
        signingStatus,
        signingError,
        signingOperationPending,
        startSigning,
        showPaymentRetry,
    } = useSigningContext(createRecurringTransferRequest());

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
            }
        },
        [checkFormValidity, state, startSigning],
    );

    useEffect(() => {
        if (account && !state.fromAccount && !state.toAccount) {
            if (isValidAccountTransferFrom(account)) {
                handleChange({
                    type: RecurringTransferActionType.ACCOUNT_SELECTED,
                    payload: { accountNumber: account.accountNumber, setAccountType: SetAccountType.SET_FROM_ACCOUNT },
                });
            }
        }
    }, [account, handleChange, state.fromAccount, state.toAccount]);

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (signingError || signingStatus === 'COMPLETED' || hasSigningError(signingStatus)) {
        return (
            <TransactionResultInfo
                type="RECURRING_TRANSFER"
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={fromAccountId.current}
                onClose={onClose}
                paymentDate={state.firstPaymentDate}
            />
        );
    }

    const isInvalidAccount = !!account && !state.fromAccount && !isValidAccountTransferFrom(account);

    if (isInvalidAccount && !state.showFromAccounts) {
        return (
            <InvalidFromAccountInfo
                onSelect={() => handleChange({ type: RecurringTransferActionType.SHOW_FROM_ACCOUNTS })}
                account={account}
                transactionType={TransactionType.TRANSFER}
            />
        );
    }

    if (canTransferMoney === CanTransferMoney.NO) {
        return <MissingToAccountInfo onSelect={() => onClose(true)} />;
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <h2 className="create-recurring-payment-form__title">{translated.recurringPayments.recurringTransfer}</h2>
            <TransactionForm eightCol onSubmit={onSubmit}>
                <RecurringTransferFields locked={signingOperationPending} />
                <TransactionSubmitWrapper className="create-recurring-payment-form__submit-container">
                    <ButtonWithSpinner
                        className="create-recurring-payment-form__submit"
                        buttonType="positive"
                        type="submit"
                        data-testid="transfer-button"
                        showSpinner={signingOperationPending}
                    >
                        {translated.recurringPayments.createTransfer}
                    </ButtonWithSpinner>
                </TransactionSubmitWrapper>
            </TransactionForm>
            <DismissKeyboardStickyFooter />
        </SigningContextFlow>
    );
};

export default CreateRecurringTransferForm;
